import { useState } from "react";
import { useEffect } from "react";
import { RootState, AppDispatch } from "../../../state/configureStore";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { SubpageTitle } from "../../../components/atoms/text/titles";
import { useMemo } from "react";
import { MachineStyle } from "../styles/machineStyle";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { EditAlertEmailModal } from "./editAlertEmailModal";
import { AddAlertModal } from "./addAlertModal";
import { DeleteAlertModal } from "./deleteAlertModal";
import { resetSavingStatus } from "../../../state/reducers/slices/machineAlertSlice";
import { AlertData, fetchAlertData, fetchAlertTypes } from "../../../state/thunks/machineAlertsThunks";

interface MachineAlertsProps {
  serialId: string;
}

const eventTypeLabels: Record<string, string> = {
  MachineDisconnected: "Machine Disconnected",
  "LiquidLevel:Low": "Liquid Level Low",
  PlateCalibrationCritical: "Critical Plate Calibration",
};

export const MachineAlerts = ({ serialId }: MachineAlertsProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { alertData, loading } = useSelector(
    (state: RootState) => state.machineAlert,
  );

  //sorting
  type Order = "asc" | "desc";
  const [orderBy, setOrderBy] = useState<keyof AlertData>("eventType");
  const [order, setOrder] = useState<Order>("asc");

  //states for modals
  const [editTarget, setEditTarget] = useState<AlertData | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AlertData | null>(null);

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (serialId) {
      dispatch(fetchAlertData(serialId));
      //make sure alertTypes are fetched and available for populate addAlertTypes modal when clicked. 
      dispatch(fetchAlertTypes());
    }

    //force the window to show topRef element
    topRef.current?.scrollIntoView({ behavior: "auto" });
  }, [dispatch, serialId]);

  //logic for sorting the table
  const sortedAlerts = useMemo(() => {
    if (!Array.isArray(alertData)) return [];

    return [...alertData].sort((a, b) => {
      const valueA = (a[orderBy] ?? "").toString().toLowerCase();
      const valueB = (b[orderBy] ?? "").toString().toLowerCase();

      if (valueB < valueA) return order === "asc" ? 1 : -1;
      if (valueB > valueA) return order === "asc" ? -1 : 1;
      return 0;
    });
  }, [alertData, order, orderBy]);

  const handleRequestSort = (property: keyof AlertData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  //open and close edit modal
  const handleOpenEdit = (alert: AlertData) => {
    dispatch(resetSavingStatus());
    setEditTarget(alert);
  };
  const handleCloseEdit = () => {
    setEditTarget(null);
  };

  //open and close add modal
  const handleOpenAdd = () => {
    dispatch(resetSavingStatus());
    setIsAddModalOpen(true);
  };
  const handleOnCloseAdd = () => setIsAddModalOpen(false);

  const handleDeleteClick = (alert: AlertData) => {
    dispatch(resetSavingStatus());
    setDeleteTarget(alert);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <EditAlertEmailModal alert={editTarget} onClose={handleCloseEdit} />
      <DeleteAlertModal
        alert={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      ></DeleteAlertModal>
      <AddAlertModal
        open={isAddModalOpen}
        onClose={handleOnCloseAdd}
        serialId={serialId}
      ></AddAlertModal>

      {sortedAlerts.length > 0 ? (
        <Box sx={{ ...MachineStyle.infoSection }}>
          <SubpageTitle title={"Machine Alerts"} />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "eventType"}
                    direction={orderBy === "eventType" ? order : "asc"}
                    onClick={() => handleRequestSort("eventType")}
                  >
                    <strong>Incident type</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "emailRecipient"}
                    direction={orderBy === "emailRecipient" ? order : "asc"}
                    onClick={() => handleRequestSort("emailRecipient")}
                  >
                    <strong>Email</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAlerts.map((alert: any) => (
                <TableRow key={alert.alertId}>
                  <TableCell>
                    {eventTypeLabels[alert.eventType] || alert.eventType}
                  </TableCell>
                  <TableCell>{alert.emailRecipient}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center", // Centrerar barnen vertikalt
                        lineHeight: 0, // Tar bort extra höjd från text-rader
                        gap: 0.5,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => handleOpenEdit(alert)}
                        sx={{
                          minWidth: "auto",
                          p: 0.5, // Lite mindre padding gör den mer kompakt
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Edit
                      </Button>
                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => handleDeleteClick(alert)}
                        sx={{ color: "black", p: 0.5 }} // Padding matchar knappen
                      >
                        <DeleteIcon
                          fontSize="small"
                          sx={{ display: "block" }}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={MachineStyle.buttonContainer}>
            <Button
              onClick={handleOpenAdd}
              startIcon={<AddIcon />}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                "& .MuiButton-startIcon": {
                  margin: 0,
                  marginRight: "8px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              Add Alert
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography>No email alerts exists for this machine.</Typography>
      )}
    </>
  );
};
